/**
 * <contact-tabs>: tab bar of the contact page (sections/contact-main.liquid) switching between the customer
 * form (first panel, same section) and the professional request (#b2b, sections/contact-b2b.liquid).
 * The visible panel follows the `ct-pro` class on <html> (contact.css), which the section's inline script
 * already sets before the first paint when the URL targets the B2B form: no flash, no layout shift.
 * Without JS there are no tabs and both forms stay on the page.
 * A highlight slides under the selected tab (`::before`, placed by `--ix` / `--iw` / `--ic`, see `place()`).
 */

const PRO = 'ct-pro';

/** Tab kept across re-renders in the theme editor. */
let current = 0;

/**
 * Index of the tab whose panel holds `target` (or sits inside it, for a whole section), -1 when none.
 *
 * @param {Element[]} tabs
 * @param {Element | null} target
 * @returns {number}
 */
export function tabFor(tabs, target) {
  if (!target) return -1;
  return tabs.findIndex((tab) => {
    const panel = document.getElementById(tab.getAttribute('aria-controls') ?? '');
    return !!panel && (panel.contains(target) || target.contains(panel));
  });
}

/** @param {string} hash */
function hashTarget(hash) {
  const id = decodeURIComponent(hash.slice(1));
  return id ? document.getElementById(id) : null;
}

export class ContactTabs extends HTMLElement {
  /** @type {ResizeObserver | undefined} */
  observer;

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    window.addEventListener('hashchange', this.onHashChange);
    document.addEventListener('shopify:section:select', this.onEditorSelect);
    document.addEventListener('shopify:block:select', this.onEditorSelect);
    // Children may not be parsed yet when the element connects.
    queueMicrotask(() => {
      for (const tab of this.tabs) {
        const panel = document.getElementById(tab.getAttribute('aria-controls') ?? '');
        panel?.setAttribute('role', 'tabpanel');
        panel?.setAttribute('aria-labelledby', tab.id);
      }
      const index = tabFor(this.tabs, hashTarget(location.hash));
      this.select(index >= 0 ? index : current);
      // Labels change width when the display font arrives, and the bar when the screen turns.
      this.observer = new ResizeObserver(() => this.place());
      this.observer.observe(this);
      // Slide only from now on: the first placement must not sweep in from the left.
      requestAnimationFrame(() => this.classList.add('is-ready'));
    });
  }

  /** Moves the highlight under the selected tab, in its colour. */
  place() {
    const tab = this.tabs[current];
    if (!tab) return;
    this.style.setProperty('--ix', `${tab.offsetLeft}px`);
    this.style.setProperty('--iw', `${tab.offsetWidth}px`);
    this.style.setProperty('--ic', tab.style.getPropertyValue('--c') || '#ffcb2e');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('hashchange', this.onHashChange);
    document.removeEventListener('shopify:section:select', this.onEditorSelect);
    document.removeEventListener('shopify:block:select', this.onEditorSelect);
    // The tabs may be switched off in the editor: never leave the customer form hidden.
    document.documentElement.classList.remove(PRO);
    this.observer?.disconnect();
  }

  /** @returns {HTMLElement[]} */
  get tabs() {
    return [...this.querySelectorAll('[role="tab"]')].filter((tab) => tab instanceof HTMLElement);
  }

  /**
   * @param {number} index 0: customer form, 1: professional request.
   * @param {{ focus?: boolean, scroll?: boolean }} [options]
   */
  select(index, { focus = false, scroll = false } = {}) {
    current = index;
    document.documentElement.classList.toggle(PRO, index === 1);
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
      if (i === index && focus) tab.focus();
    });
    this.place();
    if (scroll) this.scrollIntoView({ block: 'start' });
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    const index = tab ? this.tabs.indexOf(/** @type {HTMLElement} */ (tab)) : -1;
    if (index < 0) return;
    this.select(index);
    // Shareable URL, and a later click on a « #b2b » link fires hashchange again.
    history.replaceState(history.state, '', index === 1 ? '#b2b' : location.pathname + location.search);
  };

  /** @param {KeyboardEvent} event */
  onKeydown = (event) => {
    const tabs = this.tabs;
    const last = tabs.length - 1;
    /** @type {Record<string, number>} */
    const moves = {
      ArrowLeft: current > 0 ? current - 1 : last,
      ArrowRight: current < last ? current + 1 : 0,
      Home: 0,
      End: last,
    };
    const index = moves[event.key];
    if (index === undefined) return;
    event.preventDefault();
    this.select(index, { focus: true });
  };

  onHashChange = () => {
    const index = tabFor(this.tabs, hashTarget(location.hash));
    // The target was hidden, so the browser could not scroll to it: show the tabs instead.
    if (index >= 0) this.select(index, { scroll: true });
  };

  /** @param {Event} event */
  onEditorSelect = (event) => {
    const index = tabFor(this.tabs, event.target instanceof Element ? event.target : null);
    if (index >= 0 && index !== current) this.select(index);
  };
}

if (!customElements.get('contact-tabs')) customElements.define('contact-tabs', ContactTabs);
